import React, { Component, ReactNode } from 'react'

import { generateUniqueId } from '@sparkai/utils'

import { addLogger } from '../utils/datadogUtil'

const SparkLogo = () => (
  <svg width='100px' height='100px' viewBox='0 0 97 82' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M13.4481 0.459351C20.8725 0.459351 26.8961 6.56844 26.8961 14.0983V49.19C26.8961 56.7198 20.8725 62.8289 13.4481 62.8289C6.02362 62.8289 0 56.7198 0 49.19V14.0983C0 6.56844 6.02362 0.459351 13.4481 0.459351Z'
      fill='#111827'
    />
    <path
      d='M48.0489 19.3195C55.4734 19.3195 61.497 25.4285 61.497 32.9584V68.0501C61.497 75.5799 55.4734 81.689 48.0489 81.689C40.6244 81.689 34.6008 75.5799 34.6008 68.0501V32.9584C34.6008 25.4285 40.6244 19.3195 48.0489 19.3195V19.3195Z'
      fill='#111827'
    />
    <path
      d='M82.6496 1.91559C90.0741 1.91559 96.0977 8.02468 96.0977 15.5545V63.6458C96.0977 71.1756 90.0741 77.2847 82.6496 77.2847C75.2252 77.2847 69.2015 71.1756 69.2015 63.6458V15.5545C69.2015 8.02468 75.2252 1.91559 82.6496 1.91559V1.91559Z'
      fill='#111827'
    />
  </svg>
)

const LinkIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    fill='none'
    viewBox='0 0 24 24'
    strokeWidth='1.5'
    stroke='currentColor'
    className='h-4 w-4'
  >
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      d='M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25'
    />
  </svg>
)

const BaseErrorBody = ({ eventId }: { eventId: string | null }) => {
  return (
    <>
      <h1 className='font-inter text-2xl font-bold text-gray-900'>Oops! Something went wrong.</h1>
      <p className='mt-6 mb-6 text-center text-gray-700'>
        We found an unexpected error on the page. Please, refresh the page and try again.
      </p>

      {eventId !== null && (
        <div className='inline-flex flex-col items-center text-sm'>
          <span className='font-semibold'>Error ID</span>
          <span className='rounded-sm bg-gray-200 px-1.5 py-0.5 font-mono'>{eventId}</span>
        </div>
      )}

      <p className='mt-1 text-center text-gray-700'>
        If the error persists, please{' '}
        <a
          href='https://airtable.com/shrI5d8EEQOP2Y7Pp'
          target='_blank'
          rel='noreferrer'
          className='inline-flex items-center gap-1 text-blue-500 hover:text-blue-600 hover:underline'
        >
          click here
          <LinkIcon />
        </a>{' '}
        to report it.
      </p>
    </>
  )
}

const ErrorMessage = ({ eventId, errorMessage }: { eventId: string | null; errorMessage?: ReactNode }) => {
  return (
    <div className='h-screen bg-white'>
      <div className='container mx-auto flex h-full flex-col items-center justify-center'>
        <div>
          <SparkLogo />
        </div>
        {errorMessage || <BaseErrorBody eventId={eventId} />}
      </div>
    </div>
  )
}

interface ErrorBoundaryProps {
  children: ReactNode
  customErrorMessage?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  errorInfo: string | null
  eventId: string | null
}

export class MonitoringErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, errorInfo: null, eventId: null }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const eventId = generateUniqueId()

    this.setState({ errorInfo: error.toString(), eventId })

    const renderingError = new Error(error.message)
    renderingError.name = `Error Boundary (${eventId})`
    renderingError.stack = errorInfo.componentStack || undefined
    renderingError.cause = error

    addLogger(renderingError.name, errorInfo, renderingError)
  }

  render() {
    if (this.state.hasError)
      return <ErrorMessage eventId={this.state.eventId} errorMessage={this.props.customErrorMessage} />

    return this.props.children
  }
}
